import React, { useEffect, useRef, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { toast } from "sonner@2.0.3";
import { Upload, ExternalLink } from "lucide-react";
import { mediaService } from "../../services/Project";
import {
  INSTITUTIONAL_PROFILE_KEY,
  EXPERT_ROSTER_KEY,
  SiteDocument,
  getAdminDocuments,
  getDocumentViewUrl,
  updateDocument,
} from "../../services/Document";

const MAX_PDF_SIZE_MB = 25;

function DocumentCard({ docKey, title }: { docKey: string; title: string }) {
  const [current, setCurrent] = useState<SiteDocument | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const docs = await getAdminDocuments();
      setCurrent(docs.find((d) => d.docKey === docKey) || null);
    } catch (err: any) {
      toast.error(err?.message || "Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] || null;
    if (!selected) {
      setFile(null);
      return;
    }
    if (selected.type !== "application/pdf" && !selected.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please select a PDF file");
      e.target.value = "";
      return;
    }
    if (selected.size > MAX_PDF_SIZE_MB * 1024 * 1024) {
      toast.error(`PDF must be smaller than ${MAX_PDF_SIZE_MB} MB`);
      e.target.value = "";
      return;
    }
    setFile(selected);
  };

  const handleUpload = async () => {
    if (!file) return;
    setSaving(true);
    try {
      const uploadRes = await mediaService.uploadMedia([{ file }], { name: "documents" });
      const asset = uploadRes?.uploadedAssets?.[0];
      if (!asset?.success || !asset.fileName) {
        throw new Error(asset?.error || "Upload failed");
      }
      const saved = await updateDocument(docKey, {
        fileName: asset.fileName,
        originalFileName: file.name,
      });
      setCurrent(saved);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      toast.success(`${title} updated`);
    } catch (err: any) {
      toast.error(err?.message || "Failed to update PDF");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="text-sm">
          {loading ? (
            <p className="text-gray-500">Loading...</p>
          ) : current ? (
            <div className="space-y-1">
              <p>
                <span className="font-medium">Current file:</span>{" "}
                {current.originalFileName || current.fileName}
              </p>
              {current.updatedAt && (
                <p className="text-gray-500">
                  Last updated: {new Date(current.updatedAt).toLocaleString()}
                </p>
              )}
              <a
                href={getDocumentViewUrl(current.fileName)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-blue-600 hover:underline"
              >
                <ExternalLink className="w-4 h-4 mr-1" />
                View current PDF
              </a>
            </div>
          ) : (
            <p className="text-gray-500">
              No PDF uploaded yet. The website is using the default bundled PDF.
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor={`${docKey}-file`}>Upload new PDF (max {MAX_PDF_SIZE_MB} MB)</Label>
          <Input
            id={`${docKey}-file`}
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            onChange={handleFileChange}
            disabled={saving}
          />
        </div>

        <Button onClick={handleUpload} disabled={!file || saving} className="cursor-pointer">
          <Upload className="w-4 h-4 mr-2" />
          {saving ? "Uploading..." : "Upload & Publish"}
        </Button>
      </CardContent>
    </Card>
  );
}

export function ManageDocuments() {
  return (
    <div className="space-y-6">
      <DocumentCard docKey={INSTITUTIONAL_PROFILE_KEY} title="Institutional Profile PDF" />
      <DocumentCard docKey={EXPERT_ROSTER_KEY} title="Roster of Additional Experts and Field Researchers PDF" />
    </div>
  );
}
